import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';

// Chart URL generators
const getAxiomChartUrl = (chain, address) => {
  if (chain === 'solana' || chain === 'bsc') {
    return `https://axiom.trade/t/${address}`;
  }
  return null;
};

const getDexScreenerUrl = (chain, address) => {
  const chainMap = {
    solana: 'solana',
    base: 'base',
    ethereum: 'ethereum',
    bsc: 'bsc'
  };
  return `https://dexscreener.com/${chainMap[chain] || chain}/${address}`;
};

const getGmgnUrl = (chain, address) => {
  if (chain === 'base') {
    return `https://gmgn.ai/base/token/${address}`;
  }
  if (chain === 'ethereum') {
    return `https://gmgn.ai/eth/token/${address}`;
  }
  if (chain === 'solana') {
    return `https://gmgn.ai/sol/token/${address}`;
  }
  return null;
};

export default function Alerts() {
  const [milestones, setMilestones] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('milestones');
  const [chartModal, setChartModal] = useState({ isOpen: false, chain: '', tokenAddress: '', tokenSymbol: '' });
  const [readAlerts, setReadAlerts] = useState(() => {
    try {
      const saved = localStorage.getItem('readAlerts');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Save read alerts to localStorage
  useEffect(() => {
    localStorage.setItem('readAlerts', JSON.stringify(readAlerts));
  }, [readAlerts]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [milestonesRes, recoveriesRes] = await Promise.all([
        api.get('/analytics/milestones?limit=100'),
        api.get('/analytics/recovery-alerts?limit=100')
      ]);

      // Sort milestones by most recent first
      const sortedMilestones = (milestonesRes.data.milestones || []).sort((a, b) =>
        new Date(b.lastUpdatedAt) - new Date(a.lastUpdatedAt)
      );

      // Recovery alerts already sorted by alertSentAt from API
      const sortedRecoveries = recoveriesRes.data.alerts || [];

      setMilestones(sortedMilestones);
      setRecoveries(sortedRecoveries);
      setError(null);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const markAsRead = (type, id) => {
    const key = `${type}:${id}`;
    setReadAlerts(prev => ({ ...prev, [key]: true }));
  };

  const markAllAsRead = (type) => {
    const items = type === 'milestones' ? milestones : recoveries;
    const newReadAlerts = { ...readAlerts };
    items.forEach(item => {
      const key = `${type}:${item._id}`;
      newReadAlerts[key] = true;
    });
    setReadAlerts(newReadAlerts);
  };

  const clearReadStatus = () => {
    setReadAlerts({});
  };

  const isRead = (type, id) => {
    return readAlerts[`${type}:${id}`] === true;
  };

  const getUnreadCount = (type) => {
    const items = type === 'milestones' ? milestones : recoveries;
    return items.filter(item => !isRead(type, item._id)).length;
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  if (loading && milestones.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading alerts...</div>
      </div>
    );
  }

  const unreadMilestones = getUnreadCount('milestones');
  const unreadRecoveries = getUnreadCount('recoveries');

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Alerts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            All milestone and recovery alerts from tracked tokens
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => markAllAsRead(activeTab)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Mark All Read
          </button>
          <button
            onClick={clearReadStatus}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800/50"
          >
            Reset Read Status
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800/50 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('milestones')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
            activeTab === 'milestones'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
        >
          Milestones
          {unreadMilestones > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {unreadMilestones}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('recoveries')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
            activeTab === 'recoveries'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
        >
          Recoveries
          {unreadRecoveries > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {unreadRecoveries}
            </span>
          )}
        </button>
      </div>

      {/* Milestones Tab */}
      {activeTab === 'milestones' && (
        <div className="space-y-3">
          {milestones.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No milestone alerts yet
            </div>
          ) : (
            milestones.map((token) => (
              <div
                key={token._id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 ${
                  isRead('milestones', token._id)
                    ? 'border-gray-300 opacity-75'
                    : 'border-green-500'
                }`}
                onClick={() => markAsRead('milestones', token._id)}
              >
                <div className="flex items-start justify-between">
                  {/* Token info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {token.tokenSymbol}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {token.chain}
                      </span>
                      {!isRead('milestones', token._id) && (
                        <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 dark:text-green-300 rounded">
                          NEW
                        </span>
                      )}
                    </div>

                    {/* Milestone badges */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {token.milestones && token.milestones.map((m, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 text-sm font-semibold rounded ${
                            m.multiplier >= 10
                              ? 'bg-yellow-100 text-yellow-800 dark:text-yellow-300'
                              : m.multiplier >= 5
                              ? 'bg-green-100 text-green-800 dark:text-green-300'
                              : 'bg-blue-100 text-blue-800 dark:text-blue-300'
                          }`}
                        >
                          {m.multiplier}x
                        </span>
                      ))}
                    </div>

                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span>Initial MC: ${token.initialMarketCap?.toLocaleString() || 'N/A'}</span>
                      <span className="mx-2">|</span>
                      <span>Latest MC: ${token.latestMarketCap?.toLocaleString() || 'N/A'}</span>
                    </div>

                    {token.trackers && token.trackers.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Tracked by: {token.trackers.map(t => t.groupName || t.chatId).join(', ')}
                      </div>
                    )}
                  </div>

                  {/* Right side: Time and Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatTimeAgo(token.lastUpdatedAt)}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {formatDate(token.lastUpdatedAt)}
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex items-center gap-1">
                      {getAxiomChartUrl(token.chain, token.tokenAddress) && (
                        <a
                          href={getAxiomChartUrl(token.chain, token.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="View on Axiom"
                        >
                          AXI
                        </a>
                      )}
                      <a
                        href={getDexScreenerUrl(token.chain, token.tokenAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        title="View on DexScreener"
                      >
                        DEX
                      </a>
                      {getGmgnUrl(token.chain, token.tokenAddress) && (
                        <a
                          href={getGmgnUrl(token.chain, token.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          title="View on GMGN"
                        >
                          GMGN
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setChartModal({
                            isOpen: true,
                            chain: token.chain,
                            tokenAddress: token.tokenAddress,
                            tokenSymbol: token.tokenSymbol
                          });
                        }}
                        className="text-xs px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                        title="Open Chart Preview"
                      >
                        Chart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Recoveries Tab */}
      {activeTab === 'recoveries' && (
        <div className="space-y-3">
          {recoveries.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No recovery alerts yet
            </div>
          ) : (
            recoveries.map((recovery) => (
              <div
                key={recovery._id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 ${
                  isRead('recoveries', recovery._id)
                    ? 'border-gray-300 opacity-75'
                    : 'border-blue-500'
                }`}
                onClick={() => markAsRead('recoveries', recovery._id)}
              >
                <div className="flex items-start justify-between">
                  {/* Token info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {recovery.tokenSymbol}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {recovery.chain}
                      </span>
                      {recovery.isRecovering && (
                        <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:text-blue-300 rounded">
                          RECOVERING
                        </span>
                      )}
                      {!recovery.isRecovering && recovery.recoveryMilestonesReached?.length > 0 && (
                        <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">
                          RECOVERED
                        </span>
                      )}
                      {!isRead('recoveries', recovery._id) && (
                        <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 dark:text-green-300 rounded">
                          NEW
                        </span>
                      )}
                    </div>

                    {/* Recovery milestone badges */}
                    {recovery.recoveryMilestonesReached && recovery.recoveryMilestonesReached.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {recovery.recoveryMilestonesReached.sort((a, b) => b - a).map((pct, idx) => (
                          <span
                            key={idx}
                            className={`px-2 py-1 text-sm font-semibold rounded ${
                              pct >= 100
                                ? 'bg-green-100 text-green-800 dark:text-green-300'
                                : pct >= 50
                                ? 'bg-blue-100 text-blue-800 dark:text-blue-300'
                                : 'bg-gray-100 text-gray-800 dark:text-gray-100'
                            }`}
                          >
                            +{pct}%
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span>Bottom: ${recovery.bottomMarketCap?.toLocaleString() || 'N/A'}</span>
                      <span className="mx-2">|</span>
                      <span>Current: ${recovery.currentMarketCap?.toLocaleString() || 'N/A'}</span>
                      {recovery.recoveryPercentage && (
                        <>
                          <span className="mx-2">|</span>
                          <span className="text-green-600 font-medium">
                            +{recovery.recoveryPercentage.toFixed(1)}% from bottom
                          </span>
                        </>
                      )}
                    </div>

                    {recovery.trackers && recovery.trackers.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Tracked by: {recovery.trackers.map(t => t.groupName || t.chatId).join(', ')}
                      </div>
                    )}
                  </div>

                  {/* Right side: Time and Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right">
                      <div className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Alert sent</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatTimeAgo(recovery.alertSentAt)}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {formatDate(recovery.alertSentAt)}
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex items-center gap-1">
                      {getAxiomChartUrl(recovery.chain, recovery.tokenAddress) && (
                        <a
                          href={getAxiomChartUrl(recovery.chain, recovery.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="View on Axiom"
                        >
                          AXI
                        </a>
                      )}
                      <a
                        href={getDexScreenerUrl(recovery.chain, recovery.tokenAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        title="View on DexScreener"
                      >
                        DEX
                      </a>
                      {getGmgnUrl(recovery.chain, recovery.tokenAddress) && (
                        <a
                          href={getGmgnUrl(recovery.chain, recovery.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          title="View on GMGN"
                        >
                          GMGN
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setChartModal({
                            isOpen: true,
                            chain: recovery.chain,
                            tokenAddress: recovery.tokenAddress,
                            tokenSymbol: recovery.tokenSymbol
                          });
                        }}
                        className="text-xs px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                        title="Open Chart Preview"
                      >
                        Chart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Milestones</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{milestones.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Unread Milestones</div>
          <div className="text-2xl font-bold text-green-600">{unreadMilestones}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Recoveries</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{recoveries.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Unread Recoveries</div>
          <div className="text-2xl font-bold text-blue-600">{unreadRecoveries}</div>
        </div>
      </div>

      {/* DexScreener Chart Modal */}
      <DexScreenerModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ ...chartModal, isOpen: false })}
        chain={chartModal.chain}
        tokenAddress={chartModal.tokenAddress}
        tokenSymbol={chartModal.tokenSymbol}
      />
    </div>
  );
}
